"use client"

import { cn } from "@/lib/utils"
import { Telescope, Layers, Workflow } from "lucide-react"
import { useTranslations } from "next-intl"

const pillarsData = [
  { key: "foresight", icon: Telescope, color: "amber" as const },
  { key: "businessEngineering", icon: Layers, color: "teal" as const },
  { key: "processEngineering", icon: Workflow, color: "slate" as const }
]

const colorMap = {
  amber: {
    border: "border-amber-500/30",
    bg: "bg-amber-500/5",
    text: "text-amber-500",
    hover: "hover:border-amber-500/50 hover:bg-amber-500/10",
    glow: "group-hover:shadow-amber-500/20"
  },
  teal: {
    border: "border-teal-500/30",
    bg: "bg-teal-500/5",
    text: "text-teal-500",
    hover: "hover:border-teal-500/50 hover:bg-teal-500/10",
    glow: "group-hover:shadow-teal-500/20"
  },
  slate: {
    border: "border-slate-400/30",
    bg: "bg-slate-400/5",
    text: "text-slate-400",
    hover: "hover:border-slate-400/50 hover:bg-slate-400/10",
    glow: "group-hover:shadow-slate-400/20"
  }
}

export function MethodologySection() {
  const t = useTranslations("methodology")
  
  return (
    <section className="py-24 relative">
      {/* Section header */}
      <div className="text-center mb-16">
        <p className="text-accent-primary text-sm font-medium uppercase tracking-widest mb-3">
          {t("sectionLabel")}
        </p>
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
          {t("title")}
        </h2>
        <p className="text-muted-foreground text-lg max-w-2xl mx-auto leading-relaxed">
          {t("subtitle")}
        </p>
      </div>

      {/* Pillars grid */}
      <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
        {pillarsData.map((pillar) => {
          const Icon = pillar.icon
          const colors = colorMap[pillar.color]
          const capabilities = t.raw(`pillars.${pillar.key}.capabilities`) as string[]
          
          return (
            <div
              key={pillar.key}
              className={cn(
                "group relative rounded-2xl border p-8 transition-all duration-300",
                colors.border,
                colors.bg,
                colors.hover,
                "group-hover:shadow-lg",
                colors.glow
              )}
            >
              {/* Icon */}
              <div className={cn(
                "inline-flex items-center justify-center w-12 h-12 rounded-xl mb-6",
                colors.bg,
                colors.border,
                "border"
              )}>
                <Icon className={cn("w-6 h-6", colors.text)} />
              </div>

              {/* Content */}
              <div className="space-y-4">
                <div>
                  <p className={cn("text-xs font-medium uppercase tracking-wider mb-1", colors.text)}>
                    {t(`pillars.${pillar.key}.subtitle`)}
                  </p>
                  <h3 className="text-xl font-bold text-foreground">
                    {t(`pillars.${pillar.key}.title`)}
                  </h3>
                </div>

                <p className="text-muted-foreground text-sm leading-relaxed">
                  {t(`pillars.${pillar.key}.description`)}
                </p>

                {/* Capabilities */}
                <ul className="space-y-2 pt-4 border-t border-border/50">
                  {capabilities.map((capability: string, idx: number) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <span className={cn("mt-1.5 w-1 h-1 rounded-full flex-shrink-0", colors.text.replace("text-", "bg-"))} />
                      {capability}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )
        })}
      </div>

      {/* Connection to manifiesto */}
      <div className="mt-16">
        <div className="relative rounded-2xl border border-border/50 bg-card/30 p-8 md:p-10">
          <div className="flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
            <div className="flex-1">
              <p className="text-sm font-medium text-accent-primary uppercase tracking-wider mb-3">
                {t("whyMatters.label")}
              </p>
              <p className="text-lg md:text-xl text-foreground leading-relaxed">
                {t("whyMatters.description")}
              </p>
            </div>
            <div className="shrink-0">
              <a 
                href="/manifiesto/holistika" 
                className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-accent-primary/10 border border-accent-primary/20 text-accent-primary hover:bg-accent-primary/20 transition-colors text-sm font-medium"
              >
                {t("whyMatters.cta")}
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
